import {
  z,
  createValidationSchema,
  ARABIC_LATIN_NAME_REGEX,
} from '@tasama/utils';

const NAME_REGEX = ARABIC_LATIN_NAME_REGEX;
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const PASSPORT_ALLOWED_TYPES = [
  'application/pdf',
  'image/png',
  'image/jpeg',
  'image/jpg',
] as const;

export const PASSPORT_MAX_SIZE_BYTES = 5 * 1024 * 1024;

export const getGeneralManagerSchema = (t: (_key: string) => string) => {
  const ns = 'generalManagerInformation.validation';
  return z.object({
    firstName: z
      .string()
      .min(1, t(`${ns}.firstName.required`))
      .min(2, t(`${ns}.firstName.min`))
      .max(100, t(`${ns}.firstName.max`))
      .regex(NAME_REGEX, t(`${ns}.firstName.charset`)),

    lastName: z
      .string()
      .min(1, t(`${ns}.lastName.required`))
      .min(2, t(`${ns}.lastName.min`))
      .max(100, t(`${ns}.lastName.max`))
      .regex(NAME_REGEX, t(`${ns}.lastName.charset`)),

    email: z
      .string()
      .min(1, t(`${ns}.email.required`))
      .regex(EMAIL_REGEX, t(`${ns}.email.invalid`)),

    country: createValidationSchema.required(t(`${ns}.country.required`)),

    mobile: z
      .string()
      .trim()
      .min(1, t(`${ns}.mobile.required`)),

    homeAddress: z
      .string()
      .min(1, t(`${ns}.homeAddress.required`))
      .max(500, t(`${ns}.homeAddress.max`)),

    nationality: z
      .string()
      .min(1, t(`${ns}.nationality.required`))
      .max(100, t(`${ns}.nationality.max`)),

    passportCopy: z
      .instanceof(File, { message: t(`${ns}.passportCopy.required`) })
      .refine((f) => f.size > 0, t(`${ns}.passportCopy.required`))
      .refine(
        (f) => f.size <= PASSPORT_MAX_SIZE_BYTES,
        t(`${ns}.passportCopy.size`)
      )
      .refine(
        (f) =>
          PASSPORT_ALLOWED_TYPES.includes(
            f.type as (typeof PASSPORT_ALLOWED_TYPES)[number]
          ),
        t(`${ns}.passportCopy.type`)
      ),
  });
};

export const generalManagerSchema = getGeneralManagerSchema((key) => key);

export type GeneralManagerFormValues = z.infer<typeof generalManagerSchema>;

export const generalManagerDefaultValues: Omit<
  GeneralManagerFormValues,
  'passportCopy'
> = {
  firstName: '',
  lastName: '',
  email: '',
  country: '+966',
  mobile: '',
  homeAddress: '',
  nationality: '',
};
